import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/cn";

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Optional eyebrow rendered above the title. */
  eyebrow?: string;
  children: ReactNode;
  width?: "sm" | "md" | "lg" | "xl";
}

const widths = {
  sm: "max-w-sm",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
};

/**
 * Editorial modal. Backdrop is a heavy blur + dim so the modal sits
 * decisively over its context; the panel scales in from 0.96 with a
 * spring-feel cubic-bezier. Border + soft brass-tinted shadow give
 * the panel weight without a hard outline.
 */
export function Modal({
  open,
  onClose,
  title,
  eyebrow,
  children,
  width = "md",
}: Props) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    // Scroll-lock the body for the duration.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  // Render to document.body so the modal escapes any stacking context
  // an ancestor may have created (transform / filter / backdrop-filter
  // / contain anywhere up the tree turns `position: fixed` into
  // "fixed relative to that ancestor", which is how a modal can render
  // without being visible on the page). Portal puts us safely against
  // the viewport.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-50 grid place-items-center p-4"
      onClick={onClose}
    >
      {/* Backdrop — animated blur, separate element so we can layer
          a faint warm wash on top of the blur. */}
      <div
        className="absolute inset-0 animate-backdrop-in bg-black/60 backdrop-blur-md"
        aria-hidden
      />
      {/* Panel — capped at the viewport height with internal scroll
          on the body so a long form (e.g. Add Movie + lookup results)
          doesn't push the close button below the fold on short
          viewports / mobile. The header is sticky so the title +
          close stay visible while the body scrolls. */}
      <div
        className={cn(
          "relative flex max-h-[calc(100vh-2rem)] w-full flex-col animate-modal-in rounded-lg border border-line " +
            "bg-surface/95 shadow-elev backdrop-blur-md",
          widths[width],
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-line/60 px-5 pb-4 pt-5">
          <div className="min-w-0">
            {eyebrow && <div className="eyebrow mb-1.5">{eyebrow}</div>}
            <h2 className="font-display text-xl font-semibold leading-tight tracking-display-tight">
              {title}
            </h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="focus-ring -mr-1 -mt-1 grid h-8 w-8 place-items-center rounded-md text-foreground/55 transition hover:bg-surface-2 hover:text-foreground"
          >
            <svg
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              aria-hidden
            >
              <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 pb-5 pt-4">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
